import { useCallback, useEffect, useRef, useState } from 'react';
import { getActiveTenantId } from '@/lib/activeTenant';
import { useAuthStore } from '@/stores/auth.store';

const LOCK_PREFIX = 'notifi-stream:';
const CHANNEL_PREFIX = 'notifi-stream:';

/**
 * The lock and the channel are named per stream AND per who is looking at it.
 *
 * A BroadcastChannel reaches every tab on the origin, and a follower applies
 * what the leader sends straight into its own cache. Keyed on the stream name
 * alone, a tab that had switched tenant, started impersonating, or signed in as
 * someone else would accept the other tab's inbox and notification events and
 * cache them as its own — one tenant seeing another tenant's messages. Scoping
 * the name means those tabs simply never meet: different tenant, different
 * lock, different channel.
 */
function scopedName(name: string): string {
  const tenantId = getActiveTenantId() ?? 'no-tenant';
  const userId = useAuthStore.getState().userId ?? 'anon';
  return `${name}:${tenantId}:${userId}`;
}

/**
 * What a leader tab sends its followers: the events it read off the wire, and
 * its own connection status so every tab's indicator agrees.
 */
export type StreamBroadcast<TEvent> =
  | { kind: 'event'; event: TEvent }
  | { kind: 'status'; status: string }
  /** A tab that just became a follower, asking the leader for its status. */
  | { kind: 'hello' };

function supportsLocks(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function';
}

function supportsChannel(): boolean {
  return typeof BroadcastChannel === 'function';
}

/**
 * True when this tab is the one that should hold `name`'s connection.
 *
 * A browser allows six concurrent HTTP/1.1 connections per origin and the
 * dashboard opens three long-lived SSE streams against the API. At three
 * connections a tab, two open tabs fill the pool and every ordinary request —
 * the contacts list, a customer picker — queues without ever reaching the wire.
 * Hiding a tab is not enough: `document.hidden` is false for a tab that is
 * active in a window that merely lacks focus, so two side-by-side windows both
 * count as visible.
 *
 * So the streams are leased instead. Tabs queue on a Web Lock; exactly one
 * holds it and connects, and the rest listen on a BroadcastChannel. Three
 * connections total, however many tabs are open. When the leader closes or
 * stops wanting the lease the lock is released and the next tab in the queue
 * takes over — no timers, no polling, no gap.
 *
 * Without the Web Locks API every tab leads, which is the behaviour this
 * replaces.
 */
export function useStreamLease(name: string, want: boolean): boolean {
  const locks = supportsLocks();
  const scoped = scopedName(name);
  const [leased, setLeased] = useState(false);

  useEffect(() => {
    if (!want || !locks) return;

    const abort = new AbortController();
    // The callback resolves this to give the lock back. It is assigned inside
    // an async callback, so cleanup may run before it exists — aborting covers
    // that case, because a request that has not been granted is cancellable.
    const held: { release: (() => void) | null } = { release: null };

    void navigator.locks
      .request(`${LOCK_PREFIX}${scoped}`, { signal: abort.signal }, () => {
        setLeased(true);
        return new Promise<void>((resolve) => {
          held.release = resolve;
        });
      })
      .catch(() => {
        // AbortError: this tab gave up its place in the queue. Another tab has
        // the lease, which is the point.
      });

    return () => {
      setLeased(false);
      abort.abort();
      held.release?.();
    };
  }, [scoped, want, locks]);

  // Without the Web Locks API every tab leads, which is the behaviour this
  // replaces. Derived rather than set in an effect: a `false` on the first
  // render would read as "somebody else holds it" for one paint, and callers
  // that act on being a follower would act on a lease that was never contested.
  return locks ? leased : want;
}

/**
 * The follower half of {@link useStreamLease}: receive what the leader read,
 * and — when this tab *is* the leader — a `publish` to send it.
 *
 * A BroadcastChannel never delivers a tab its own messages, so the leader
 * applies its events directly and publishes them for everyone else.
 */
export function useStreamBroadcast<TEvent>(
  name: string,
  onBroadcast: (message: StreamBroadcast<TEvent>) => void,
): (message: StreamBroadcast<TEvent>) => void {
  const handler = useRef(onBroadcast);
  handler.current = onBroadcast;
  const channel = useRef<BroadcastChannel | null>(null);

  const scoped = scopedName(name);
  useEffect(() => {
    if (!supportsChannel()) return;
    const ch = new BroadcastChannel(`${CHANNEL_PREFIX}${scoped}`);
    ch.onmessage = (event: MessageEvent) => {
      handler.current(event.data as StreamBroadcast<TEvent>);
    };
    channel.current = ch;
    return () => {
      ch.close();
      channel.current = null;
    };
  }, [scoped]);

  return useCallback((message: StreamBroadcast<TEvent>) => {
    channel.current?.postMessage(message);
  }, []);
}

/**
 * {@link useStreamLease} and {@link useStreamBroadcast} wired together: this
 * tab either holds the connection and republishes what it reads, or it holds
 * none and applies what the leader publishes. Either way the hook's consumers
 * see the same events and the same status.
 *
 * A tab that becomes a follower says hello, because the leader may have gone
 * `open` long before this tab existed and a status is only broadcast when it
 * changes.
 */
export function useLeasedStream<TEvent>(params: {
  /** Shared across tabs; the lock and the channel are both named after it. */
  name: string;
  /** Whether this tab wants to hold the connection at all. */
  want: boolean;
  /** This tab's current status, republished to followers while leading. */
  status: string;
  applyEvent: (event: TEvent) => void;
  applyStatus: (status: string) => void;
}): { leader: boolean; publishEvent: (event: TEvent) => void } {
  const { name, want, status, applyEvent, applyStatus } = params;
  const leader = useStreamLease(name, want) && want;

  const wantRef = useRef(want);
  wantRef.current = want;
  const leaderRef = useRef(leader);
  leaderRef.current = leader;
  const statusRef = useRef(status);
  statusRef.current = status;
  const applyEventRef = useRef(applyEvent);
  applyEventRef.current = applyEvent;
  const applyStatusRef = useRef(applyStatus);
  applyStatusRef.current = applyStatus;

  const publish = useStreamBroadcast<TEvent>(name, (message) => {
    if (!wantRef.current) return;
    if (leaderRef.current) {
      if (message.kind === 'hello') {
        publishRef.current({ kind: 'status', status: statusRef.current });
      }
      return;
    }
    if (message.kind === 'event') applyEventRef.current(message.event);
    else if (message.kind === 'status') applyStatusRef.current(message.status);
  });
  const publishRef = useRef(publish);
  publishRef.current = publish;

  // Announce a new follower so the leader replays where it stands.
  useEffect(() => {
    if (leader || !want) return;
    publishRef.current({ kind: 'hello' });
  }, [leader, want]);

  // Keep followers' indicators in step with the connection they are relying on.
  useEffect(() => {
    if (!leader || !want) return;
    publishRef.current({ kind: 'status', status });
  }, [leader, status, want]);

  const publishEvent = useCallback((event: TEvent) => {
    if (!wantRef.current || !leaderRef.current) return;
    publishRef.current({ kind: 'event', event });
  }, []);

  return { leader, publishEvent };
}
